import store from '../../App/store';
import { ANALYTICS_ENDPOINT } from '../../utils/eventLogAnalytics';
import { apiUrl } from '../../utils/baseUrl';
import { isLayoutUuid } from './layoutLinkUtils';
import {
  LISTING_STATUS_LABELS,
  LISTING_STATUSES,
  resolveListingDisplayStatus,
} from './listingDisplayStatus';

export { LISTING_STATUS_LABELS, resolveListingDisplayStatus };

const partnerQuery = (partner) =>
  `partner=${encodeURIComponent(partner)}&partnerName=${encodeURIComponent(partner)}`;

export function analyticsAuthHeaders() {
  try {
    const currentToken = store.getState()?.roomNateSlice?.currentToken;
    if (!currentToken) return {};
    const stripped = String(currentToken).replace(/["]/g, '');
    if (!stripped) return {};
    return { Authorization: `Bearer ${stripped}` };
  } catch {
    return {};
  }
}

export const parseSignupBreakdown = (data) => {
  const b = data?.breakdown;
  if (!b || typeof b !== 'object') return null;
  return {
    editor: Number(b.editor) || 0,
    direct: Number(b.direct) || 0,
    unknown: Number(b.unknown) || 0,
    total: Number(b.total) || 0,
  };
};

export async function fetchSignupsBreakdown(dateParams, partner) {
  const res = await fetch(
    `${ANALYTICS_ENDPOINT}/signups-breakdown?${dateParams}&${partnerQuery(partner)}`,
    { headers: analyticsAuthHeaders() }
  );
  if (!res.ok) return null;
  const data = await res.json();
  return parseSignupBreakdown(data);
}

const toNonNegInt = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.floor(n);
};

const toOptionalInt = (v) => {
  if (v == null || v === '') return null;
  const n = Number(v);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.floor(n);
};

const pickField = (row, ...keys) => {
  for (const key of keys) {
    if (row[key] != null && row[key] !== '') return row[key];
  }
  return null;
};

/**
 * Normalize one `/listings-summary` row so UI always sees a stable shape.
 * Accepts snake_case or camelCase from BE; missing fields get safe defaults.
 */
export function normalizeListingSummaryRow(raw, { mlsSyncEnabled = false } = {}) {
  if (!raw || typeof raw !== 'object') return null;
  const customerRef = pickField(raw, 'customer_ref', 'customerRef');
  const layoutUuid = pickField(raw, 'layoutUuid', 'layout_uuid');
  if (!customerRef && !layoutUuid) return null;

  const statusRaw = String(pickField(raw, 'status') || 'unknown').toLowerCase().trim();
  const mlsStatus = LISTING_STATUSES.has(statusRaw) ? statusRaw : 'unknown';
  const customPropertyStatusRaw = pickField(
    raw,
    'custom_property_status',
    'customPropertyStatus'
  );
  const customPropertyStatus =
    customPropertyStatusRaw != null && customPropertyStatusRaw !== ''
      ? String(customPropertyStatusRaw).trim()
      : null;
  const rowMlsSyncRaw = pickField(raw, 'mls_sync_enabled', 'mlsSyncEnabled');
  const listingMlsSyncEnabled =
    rowMlsSyncRaw != null ? Boolean(rowMlsSyncRaw) : false;
  const display = resolveListingDisplayStatus({
    customPropertyStatus,
    mlsStatus,
    mlsSyncEnabled: listingMlsSyncEnabled,
  });

  const shareCount = toNonNegInt(
    pickField(raw, 'share_count', 'shareCount') ?? pickField(raw, 'shares')
  );
  const downloadCount = toNonNegInt(
    pickField(raw, 'download_count', 'downloadCount') ?? pickField(raw, 'downloads')
  );
  const shareCountTotal = toOptionalInt(
    pickField(raw, 'share_count_total', 'shareCountTotal') ?? pickField(raw, 'shares_total', 'sharesTotal')
  );
  const downloadCountTotal = toOptionalInt(
    pickField(raw, 'download_count_total', 'downloadCountTotal') ??
      pickField(raw, 'downloads_total', 'downloadsTotal')
  );

  return {
    customer_ref: customerRef ? String(customerRef).trim() : '—',
    layoutUuid: layoutUuid ? String(layoutUuid).trim() : null,
    status: display.status,
    statusLabel: display.statusLabel,
    statusSource: display.statusSource,
    mlsStatus: display.mlsStatus,
    customPropertyStatus: display.customPropertyStatus,
    customPropertyStatusSetAt:
      pickField(raw, 'custom_property_status_set_at', 'customPropertyStatusSetAt') || null,
    onMarketDate: pickField(raw, 'on_market_date', 'onMarketDate') || null,
    onMarketDateSource:
      pickField(raw, 'on_market_date_source', 'onMarketDateSource') || null,
    offMarketDate: pickField(raw, 'off_market_date', 'offMarketDate') || null,
    mlsSyncEnabled: listingMlsSyncEnabled,
    statusChangedAt: pickField(raw, 'status_changed_at', 'statusChangedAt') || null,
    daysOnMarket: toOptionalInt(pickField(raw, 'days_on_market', 'daysOnMarket')),
    inquiryCount: toNonNegInt(pickField(raw, 'inquiry_count', 'inquiryCount')),
    inquiryCountTotal: toOptionalInt(
      pickField(raw, 'inquiry_count_total', 'inquiryCountTotal')
    ),
    galleryViews: toNonNegInt(pickField(raw, 'galleryViews', 'gallery_views')),
    galleryViewsTotal: toOptionalInt(
      pickField(raw, 'gallery_views_total', 'galleryViewsTotal')
    ),
    sessions: toNonNegInt(pickField(raw, 'sessions')),
    sessionsTotal: toOptionalInt(pickField(raw, 'sessions_total', 'sessionsTotal')),
    shuffles: toNonNegInt(pickField(raw, 'shuffles')),
    shufflesTotal: toOptionalInt(pickField(raw, 'shuffles_total', 'shufflesTotal')),
    shares: shareCount,
    downloads: downloadCount,
    shareCount,
    downloadCount,
    shareCountTotal,
    downloadCountTotal,
    lastActivityAt: pickField(raw, 'lastActivityAt', 'last_activity_at') || null,
  };
}

export function parseListingsSummary(data) {
  if (!data || typeof data !== 'object') return null;
  const mlsSyncEnabled = Boolean(data.mls_sync_enabled ?? data.mlsSyncEnabled);
  const rawListings = Array.isArray(data.listings) ? data.listings : [];
  const listings = rawListings
    .map((row) => normalizeListingSummaryRow(row, { mlsSyncEnabled }))
    .filter(Boolean);
  const total = Number.isFinite(Number(data.total)) ? Number(data.total) : listings.length;
  return { listings, total, mlsSyncEnabled };
}

/**
 * @param {string} partner
 * @param {{ dateParams?: string|null, limit?: number }} [opts]
 *   Pass `dateParams` for the selected window; omit for all-time totals.
 */
export async function fetchListingsSummary(partner, { dateParams = null, limit = 500 } = {}) {
  const rangeQuery = dateParams ? `${dateParams}&` : '';
  const res = await fetch(
    `${ANALYTICS_ENDPOINT}/listings-summary?${rangeQuery}${partnerQuery(partner)}&limit=${limit}`,
    { headers: analyticsAuthHeaders() }
  );
  if (!res.ok) return null;
  const data = await res.json();
  return parseListingsSummary(data);
}

export async function fetchGalleryInquiries(partner, { dateParams = null, customerRef = null, limit = 100 } = {}) {
  const rangeQuery = dateParams ? `${dateParams}&` : '';
  const refQuery = customerRef ? `&customer_ref=${encodeURIComponent(customerRef)}` : '';
  const res = await fetch(
    `${apiUrl('/gallery-inquiries')}?${rangeQuery}${partnerQuery(partner)}&limit=${limit}${refQuery}`,
    { headers: analyticsAuthHeaders() }
  );
  if (!res.ok) return null;
  const data = await res.json();
  return {
    inquiries: Array.isArray(data?.inquiries) ? data.inquiries : [],
    total: Number(data?.total) || 0,
  };
}

export async function fetchListingLayoutLinks(partner, { includeLayout = true } = {}) {
  const layoutParam = includeLayout ? '&includeLayout=1' : '';
  const res = await fetch(
    `${ANALYTICS_ENDPOINT}/listing-layout-links?${partnerQuery(partner)}${layoutParam}`,
    { headers: analyticsAuthHeaders() }
  );
  if (res.status === 404) return [];
  if (!res.ok) return [];
  const data = await res.json();
  if (Array.isArray(data)) return data;
  return data?.links || [];
}

/**
 * Free-text search over the partner's listing source, proxied by the BE so the
 * upstream API key stays server-side.
 *
 * Resolves rather than throws on every failure: this backs a typeahead, where
 * a dead request should quietly yield no suggestions instead of surfacing an
 * error over a field the user can still type into by hand. `available: false`
 * means the environment has no listing source wired up, and the caller should
 * stop asking.
 */
export async function searchPartnerListings(partner, q, { limit = 8, signal } = {}) {
  const query = String(q || '').trim();
  if (!partner || !query) return { available: true, listings: [], reason: 'query_too_short' };

  let res;
  try {
    res = await fetch(
      `${ANALYTICS_ENDPOINT}/listing-search?${partnerQuery(partner)}&q=${encodeURIComponent(
        query
      )}&limit=${limit}`,
      { headers: analyticsAuthHeaders(), signal }
    );
  } catch {
    return { available: true, listings: [], reason: 'request_failed' };
  }

  if (!res.ok) {
    return { available: true, listings: [], reason: `http_${res.status}` };
  }

  const data = await res.json().catch(() => null);
  if (!data || typeof data !== 'object') {
    return { available: true, listings: [], reason: 'bad_payload' };
  }

  return {
    available: data.available !== false,
    listings: Array.isArray(data.listings) ? data.listings.filter((row) => row?.listingRef) : [],
    reason: data.reason || null,
  };
}

/** Resolve layoutUuid for POST link when catalog row lacks uuid (legacy layouts). */
export async function resolveLayoutUuidForPlan(partner, plan) {
  if (plan?.uuid && isLayoutUuid(plan.uuid)) return String(plan.uuid).trim();
  const mongoId = plan?._id;
  if (!mongoId) throw new Error('This floor plan is missing an ID. Try again or contact support.');
  const url = `${window.location.origin}/floor-gallery/${mongoId}`;
  const result = await resolveLayoutFromUrl(partner, url);
  const uuid = result?.layoutUuid || result?.uuid;
  if (!uuid) throw new Error('Could not resolve this floor plan for linking.');
  return String(uuid).trim();
}

export async function resolveLayoutFromUrl(partner, urlOrId) {
  const res = await fetch(`${ANALYTICS_ENDPOINT}/listing-layout-links/resolve-layout`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...analyticsAuthHeaders(),
    },
    body: JSON.stringify({
      partner,
      url: String(urlOrId).trim(),
      layoutUrl: String(urlOrId).trim(),
    }),
  });
  const body = await res.json().catch(() => ({}));
  if (res.status === 404) {
    throw new Error(
      'We could not find that floor plan link. Check the MagicFurnish link and try again.'
    );
  }
  if (!res.ok) {
    const msg = body?.error || body?.message || 'Could not resolve that floor plan link.';
    throw new Error(typeof msg === 'string' ? msg : 'Could not resolve that floor plan link.');
  }
  return body;
}

/**
 * Normalize the `/listing-layout-links/resolve-layout` response into the same
 * shape the library and AttachListingModal consume from `normalizePartnerLayout`.
 * BE returns `savedLayoutId` / `displayName`; FE expects `_id` / `name` (and the
 * library elsewhere reads `uuid`, `thumbnail`, `isFork`, etc.) — aliasing here
 * keeps every consumer pointed at one shape regardless of the source endpoint.
 *
 * Returns null for unrecognized payloads so callers can show a friendly error
 * instead of rendering garbage.
 */
export function normalizeResolvedLayout(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const layoutUuid = raw.layoutUuid && isLayoutUuid(raw.layoutUuid)
    ? String(raw.layoutUuid).trim()
    : null;
  if (!layoutUuid) return null;
  const name = (raw.displayName || raw.name || '').toString().trim() || 'Floor plan';
  const mongoId = raw.savedLayoutId || raw.mongoId || raw._id || null;
  const customerRef = raw.customerRef || raw.customer_ref || null;
  return {
    _id: mongoId ? String(mongoId) : null,
    uuid: layoutUuid,
    name,
    thumbnail: raw.thumbnail || null,
    uniqueId: raw.uniqueId || null,
    customerRef: customerRef ? String(customerRef) : null,
    isFork: Boolean(raw.isFork),
    rootLayoutUuid: raw.rootLayoutUuid || null,
    // `createdAt` and `copiedFromName` are not returned by resolve-layout;
    // AttachListingModal already treats them as optional.
    createdAt: null,
    copiedFromName: null,
  };
}

export async function createListingLayoutLink({
  customer_ref,
  layoutUuid,
  partner,
  backfillEvents = true,
  customPropertyStatus = null,
  onMarketDate = null,
  offMarketDate = null,
  autoSyncListing = false,
}) {
  const payload = {
    customer_ref: String(customer_ref).trim(),
    layoutUuid: String(layoutUuid).trim(),
    partner,
    backfillEvents: Boolean(backfillEvents),
  };
  if (autoSyncListing) {
    payload.autoSyncListing = true;
  }
  if (customPropertyStatus) {
    payload.customPropertyStatus = String(customPropertyStatus).trim();
  }
  if (onMarketDate) {
    payload.onMarketDate = String(onMarketDate).trim();
  }
  if (offMarketDate) {
    payload.offMarketDate = String(offMarketDate).trim();
  }

  const res = await fetch(`${ANALYTICS_ENDPOINT}/listing-layout-links`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...analyticsAuthHeaders(),
    },
    body: JSON.stringify(payload),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = body?.error || body?.message || res.statusText || 'Could not save link';
    throw new Error(typeof msg === 'string' ? msg : 'Could not save link');
  }
  return body;
}

export async function patchListingLayoutLink(linkId, partner, body = {}) {
  const res = await fetch(
    `${ANALYTICS_ENDPOINT}/listing-layout-links/${encodeURIComponent(linkId)}?${partnerQuery(partner)}`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...analyticsAuthHeaders(),
      },
      body: JSON.stringify({ partner, ...body }),
    }
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = data?.error || data?.message || res.statusText || 'Could not update listing';
    throw new Error(typeof msg === 'string' ? msg : 'Could not update listing');
  }
  return data;
}

export async function deleteListingLayoutLink(linkId, partner) {
  const res = await fetch(
    `${ANALYTICS_ENDPOINT}/listing-layout-links/${encodeURIComponent(linkId)}?${partnerQuery(partner)}`,
    { method: 'DELETE', headers: analyticsAuthHeaders() }
  );
  if (res.status === 404) return { ok: false };
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.error || body?.message || 'Could not remove link');
  }
  const body = await res.json().catch(() => ({}));
  return body?.ok === true ? body : { ok: true };
}
